import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';

import { SpinnerService } from '@app/shared/spinner';
import { forkJoin, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '@app/core/user.service';
import {
  SuperHttpClientProvider,
  HttpCriteria
} from '@app/core/http';
import {
  SensorMoistureZonesService,
  MoistureZonesResponse
} from '@app/core/sensor-moisture-zones.service';

import * as Highcharts from 'highcharts';
import * as mapboxgl from 'mapbox-gl';
import { environment } from '@env/environment';

@Component({
  standalone: false,
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('mapContainer', { static: false })
  mapContainer?: ElementRef<HTMLDivElement>;

  public fincas: any[] = [];
  public fincaSelected = -1;

  public modulos: any[] = [];
  public moduloSelected = -1;

  public weatherForecast: any[] = [];
  public soilMoistureValue: number | null = null;
  public temperatureValue: number | null = null;
  public windValue: number | null = null;
  public dailyIrrigationValue: number | null = null;
  public dailyRainValue: number | null = null;
  public digitalValue: number | null = null;
  public showHumidity = false;
  public showTemperature = false;
  public showWind = false;
  public showIrrigation = false;
  public showRain = false;
  public showDigital = false;
  public showConductivity = false;
  public humiditySensorId: number | null = null;
  public humidityReadingAt: string | null = null;

  public selectedRange = '24h';
  public rangeOptions = [
    { value: '24h', labelKey: 'HOME.RANGE.LAST_24H' },
    { value: '7d', labelKey: 'HOME.RANGE.LAST_7D' },
    { value: '30d', labelKey: 'HOME.RANGE.LAST_30D' }
  ];

  public conductividad: number | null = null;
  public conductividadSolution: number | null = null;
  public isLoading = false;
  public errorMsg = '';
  public lastUpdatedText = '';
  public isAdminUser = false;
  public sensorChartHasData = false;

  private sensorChart: Highcharts.Chart | null = null;
  private dailySeries: Highcharts.SeriesOptionsType[] = [];
  private chartRenderId = 0;
  private chartRenderHandle: any = null;
  private lastDailyData: any = null;
  private readonly themeChangeHandler = () => this.renderSensorChart();
  private getCssVar(name: string, fallback: string): string {
    if (typeof document === 'undefined') {
      return fallback;
    }
    const value = getComputedStyle(document.body)
      .getPropertyValue(name)
      .trim();
    return value || fallback;
  }

  // Mapbox
  private map: mapboxgl.Map | null = null;
  private markers: mapboxgl.Marker[] = [];
  private mapReady = false;
  private moistureZonesCache = new Map<number, MoistureZonesResponse | null>();
  private moistureZonesPending = new Set<number>();
  private moistureZonesDeferredMap = new Set<number>();
  private moistureZonesDeferredChart = new Set<number>();
  private readonly mapboxToken = environment.mapboxToken || '';
  private readonly fallbackMarkerColor = '#64748b';
  private readonly defaultHuZones = [
    { value: 0, color: '#d32f2f' },
    { value: 50, color: '#f57c00' },
    { value: 75, color: '#fbc02d' },
    { value: 100, color: '#2e7d32' },
    { color: '#1565c0' }
  ];
  private readonly weatherIconBase = 'assets/weather-icons/amcharts-animated';

  private readonly mapStyle = 'mapbox://styles/mapbox/satellite-streets-v12';
  private readonly defaultCenter: [number, number] = [-3, 40];
  private readonly defaultZoom = 6;

  constructor(
    private $http: SuperHttpClientProvider,
    private spinner: SpinnerService,
    private translate: TranslateService,
    private userService: UserService,
    private sensorMoistureZonesService: SensorMoistureZonesService
  ) {}

  ngOnInit() {
    this.isAdminUser = this.userService.getRoleId() === 1;
    this.userService.roleId$.subscribe(roleId => {
      this.isAdminUser = roleId === 1;
    });
    this.setHighchartOptions();
    this.loadFincas();

    this.translate.onLangChange.subscribe(() => {
      this.setHighchartOptions();
      if (this.lastDailyData) {
        this.dailySeries = this.buildDailySeries(this.lastDailyData);
      }
      this.renderSensorChart();
    });

    if (typeof window !== 'undefined') {
      window.addEventListener('app-theme-change', this.themeChangeHandler);
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.renderMap();
      this.renderSensorChart();
    }, 200);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.renderSensorChart();
    if (this.map) {
      this.map.resize();
      this.updateMarkersOnMap();
    }
  }

  ngOnDestroy(): void {
    if (this.chartRenderHandle) {
      clearTimeout(this.chartRenderHandle);
      this.chartRenderHandle = null;
    }
    this.destroySensorChart();
    this.destroyMap();
    if (typeof window !== 'undefined') {
      window.removeEventListener('app-theme-change', this.themeChangeHandler);
    }
  }

  get hasValidCoords(): boolean {
    return this.modulos.some(
      modulo =>
        this.toNumber(modulo.latitude) !== null &&
        this.toNumber(modulo.longitude) !== null
    );
  }

  public onSelectOpened(opened: boolean): void {
    if (!opened || typeof window === 'undefined') {
      return;
    }
    window.dispatchEvent(new CustomEvent('app-overlay-open'));
  }

  public onRangeChange(value: string) {
    this.selectedRange = value;
    if (this.moduloSelected !== -1) {
      this.moduloChange(this.moduloSelected);
    } else {
      this.renderSensorChart();
    }
  }

  public fincaChange(value: any) {
    const fincaId = Number(value);
    this.fincaSelected = fincaId;

    if (fincaId === -1) {
      this.moduloSelected = -1;
      this.lastDailyData = null;
      this.dailySeries = [];
      this.weatherForecast = [];
      this.renderSensorChart();
      this.loadModulos(-1);
      return;
    }

    this.loadModulos(fincaId);
  }

  public moduloChange(value: any) {
    const moduloId = Number(value);
    this.moduloSelected = moduloId;

    if (moduloId === -1) {
      this.weatherForecast = [];
      this.spinner.hide();
      return;
    }

    this.isLoading = true;
    this.spinner.show();
    this.errorMsg = '';
    const range = this.selectedRange || '24h';
    const url = `/last/collection/${moduloId}?range=${encodeURIComponent(
      range
    )}`;

    this.$http.default.get(url).subscribe(
      d => {
        this.lastDailyData = d?.daily || {};
        this.updateSensorAvailability(d);
        this.dailySeries = this.buildDailySeries(this.lastDailyData);
        this.updateMetrics(d);
        this.updateModuleStats(moduloId, d);
        const prediction = d?.datosPrediccion ?? d?.datosAemet ?? null;
        this.weatherForecast = this.normalizeForecastList(prediction);
        this.lastUpdatedText = new Date().toLocaleString();
        this.isLoading = false;
        this.spinner.hide();
        this.updateMarkersOnMap();
      },
      err => {
        console.error('Error cargando colección', err);
        this.errorMsg =
          'No se pudieron cargar los datos del módulo. Inténtalo de nuevo.';
        this.isLoading = false;
        this.spinner.hide();
      }
    );
  }

  private loadFincas() {
    this.isLoading = true;
    this.spinner.show();
    this.errorMsg = '';
    this.$http.default.get('/admin/finca').subscribe(
      d => {
        this.fincas = (d || []).filter(
          f => !f.isDeleted && String(f.isDeleted) !== 'true'
        );
        this.fincaChange(this.fincas.length > 0 ? this.fincas[0].idFinca : -1);
      },
      err => {
        console.error('Error cargando fincas', err);
        this.errorMsg = 'No se pudieron cargar las fincas. Inténtalo de nuevo.';
        this.spinner.hide();
        this.isLoading = false;
      }
    );
  }

  private loadModulos(fincaId: number) {
    const criteria = new HttpCriteria({
      _filters: [],
      _perPage: -1
    });
    if (fincaId !== -1) {
      criteria.addFilter({ field: 'idFinca', operator: '=', value: fincaId });
    }

    this.isLoading = true;
    this.spinner.show();
    this.errorMsg = '';
    this.$http.default.filter('/admin/module', criteria).subscribe(
      d => {
        this.modulos = (d || []).filter(
          mod => !mod.isDeleted && String(mod.isDeleted) !== 'true'
        );
        if (fincaId === -1) {
          this.moduloSelected = -1;
          this.loadAllModuleSummaries();
        } else {
          this.moduloChange(
            this.modulos.length > 0 ? this.modulos[0].idModule : -1
          );
          this.loadModuleSummaries(fincaId);
        }
        this.spinner.hide();
        this.isLoading = false;
        this.updateMarkersOnMap();
      },
      err => {
        console.error('Error cargando módulos', err);
        this.errorMsg =
          'No se pudieron cargar los módulos. Revisa la conexión e inténtalo más tarde.';
        this.spinner.hide();
        this.isLoading = false;
      }
    );
  }

  public getForecastIconUrl(day: any): string {
    const iconName = this.resolveForecastIconName(day);
    if (iconName) {
      return `${this.weatherIconBase}/${iconName}.svg`;
    }
    return `${this.weatherIconBase}/weather.svg`;
  }

  private resolveForecastIconName(day: any): string | null {
    const weatherCode = this.toNumber(day?.weatherCode);
    const text = this.normalizeConditionText(
      day?.conditionText ??
        day?.condition?.text ??
        day?.text ??
        day?.condition?.description
    );
    const isNight = this.isNightForecast(day);
    const rainProb = this.toNumber(day?.rain);
    const rainMm = this.toNumber(day?.rainMm);

    const weatherCodeIcon = this.resolveForecastIconFromWeatherCode(
      weatherCode,
      isNight
    );
    if (weatherCodeIcon) {
      return weatherCodeIcon;
    }

    if (this.hasAny(text, ['tormenta', 'thunder', 'storm'])) {
      return 'thunder';
    }
    if (this.hasAny(text, ['nieve', 'snow', 'sleet'])) {
      return 'snowy-3';
    }
    if (
      this.hasAny(text, [
        'lluv',
        'rain',
        'shower',
        'chubasco',
        'drizzle',
        'llovizna'
      ])
    ) {
      if (this.hasAny(text, ['fuerte', 'heavy', 'intense', 'torrential'])) {
        return 'rainy-6';
      }
      if (this.hasAny(text, ['debil', 'light'])) {
        return 'rainy-2';
      }
      if (this.hasAny(text, ['moderad', 'moderate'])) {
        return 'rainy-3';
      }
      return 'rainy-4';
    }
    if ((rainMm ?? 0) > 0 || (rainProb ?? 0) >= 50) {
      if ((rainMm ?? 0) >= 8 || (rainProb ?? 0) >= 85) {
        return 'rainy-6';
      }
      if ((rainMm ?? 0) >= 2 || (rainProb ?? 0) >= 65) {
        return 'rainy-4';
      }
      return 'rainy-2';
    }
    if (this.hasAny(text, ['despej', 'clear', 'sunny'])) {
      return isNight ? 'night' : 'day';
    }
    if (this.hasAny(text, ['parcial', 'partly', 'intervals', 'poco nuboso'])) {
      return isNight ? 'cloudy-night-1' : 'cloudy-day-1';
    }
    if (
      this.hasAny(text, [
        'nublado',
        'cloud',
        'overcast',
        'cubierto',
        'fog',
        'bruma',
        'mist'
      ])
    ) {
      return isNight ? 'cloudy-night-2' : 'cloudy-day-2';
    }
    return isNight ? 'night' : 'day';
  }

  private resolveForecastIconFromWeatherCode(
    weatherCode: number | null,
    isNight: boolean
  ): string | null {
    if (weatherCode === null) {
      return null;
    }

    if (weatherCode === 0) {
      return isNight ? 'night' : 'day';
    }
    if (weatherCode === 1) {
      return isNight ? 'cloudy-night-1' : 'day';
    }
    if (weatherCode === 2) {
      return isNight ? 'cloudy-night-1' : 'cloudy-day-1';
    }
    if (weatherCode === 3) {
      return isNight ? 'cloudy-night-2' : 'cloudy-day-2';
    }
    if (weatherCode === 45 || weatherCode === 48) {
      return 'cloudy';
    }
    if ([51, 53, 56].includes(weatherCode)) {
      return 'rainy-2';
    }
    if ([55, 57, 61, 63, 66, 80, 81].includes(weatherCode)) {
      return 'rainy-4';
    }
    if ([65, 67, 82].includes(weatherCode)) {
      return 'rainy-6';
    }
    if ([71, 73, 77, 85].includes(weatherCode)) {
      return 'snowy-3';
    }
    if ([75, 86].includes(weatherCode)) {
      return 'snowy-6';
    }
    if ([95, 96, 99].includes(weatherCode)) {
      return 'thunder';
    }

    return null;
  }

  private isNightForecast(day: any): boolean {
    const flag = day?.isDay ?? day?.is_day ?? day?.isDaylight;
    if (flag === false || flag === 0) return true;
    if (flag === true || flag === 1) return false;
    const raw = day?.conditionIcon || day?.condition?.icon;
    if (typeof raw === 'string') {
      const iconLower = raw.toLowerCase();
      if (iconLower.includes('night')) return true;
      if (iconLower.includes('day')) return false;
    }
    return false;
  }

  private normalizeConditionText(value: any): string {
    if (!value || typeof value !== 'string') return '';
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  private hasAny(text: string, tokens: string[]): boolean {
    if (!text) return false;
    return tokens.some(token => text.includes(token));
  }

  private normalizeForecastList(data: any): any[] {
    if (!Array.isArray(data)) return [];
    return data
      .map(entry => this.normalizeForecastDay(entry))
      .filter(day => !!day);
  }

  private normalizeForecastDay(entry: any): any | null {
    if (!entry) return null;
    const date =
      entry.date ??
      entry.day ??
      entry.fecha ??
      entry.dayDate ??
      entry.time ??
      entry.timestamp ??
      null;
    const min = this.toNumber(
      entry.min ??
        entry.minTemp ??
        entry.tempMin ??
        entry.temperatureMin ??
        entry.tmin ??
        entry.temperatura?.minima
    );
    const max = this.toNumber(
      entry.max ??
        entry.maxTemp ??
        entry.tempMax ??
        entry.temperatureMax ??
        entry.tmax ??
        entry.temperatura?.maxima
    );
    const rain =
      this.toNumber(
        entry.rain ??
          entry.rainProb ??
          entry.rainProbability ??
          entry.precipProb ??
          entry.probRain ??
          entry.probPrecipitacion?.[0]?.value
      ) ?? null;
    const rainMm = this.toNumber(
      entry.rainMm ??
        entry.precipMm ??
        entry.precipitationMm ??
        entry.precipitation ??
        entry.rainAmount ??
        entry.precipitacion
    );
    const windDir =
      entry.windDir ??
      entry.windDirection ??
      entry.wind_direction ??
      entry.dirViento ??
      entry.wind?.dir ??
      entry.viento?.[0]?.direccion;
    const windKph = this.toNumber(
      entry.windKph ??
        entry.windSpeed ??
        entry.windSpeedKph ??
        entry.wind_kph ??
        entry.wind?.speed ??
        entry.viento?.[0]?.velocidad ??
        entry.viento
    );
    const conditionText =
      entry.conditionText ??
      entry.condition ??
      entry.conditionDesc ??
      entry.conditionDescription ??
      entry.descripcion ??
      entry.text;
    const conditionIcon =
      entry.conditionIcon ??
      entry.icon ??
      entry.condition?.icon ??
      entry.iconUrl;
    const weatherCode = this.toNumber(
      entry.weatherCode ?? entry.weather_code ?? entry.weathercode
    );

    return {
      date,
      min,
      max,
      rain,
      rainMm,
      windDir,
      windKph,
      conditionText,
      conditionIcon,
      weatherCode
    };
  }

  private loadModuleSummaries(fincaId: number) {
    if (!fincaId || fincaId === -1) return;

    this.$http.default
      .get(
        `/admin/module/summary?fincaId=${encodeURIComponent(String(fincaId))}`
      )
      .pipe(
        tap((summaries: any[]) => {
          if (!Array.isArray(summaries)) return;
          const byId = new Map<number, any>();
          summaries.forEach(item => {
            const id = Number(item?.idModule);
            if (!Number.isFinite(id)) return;
            byId.set(id, item);
          });

          this.modulos = this.modulos.map(mod => {
            const summary = byId.get(Number(mod.idModule));
            if (!summary) return mod;
            return {
              ...mod,
              batteryPercent: summary.batteryPercent ?? mod.batteryPercent,
              signalPercent: summary.signalPercent ?? mod.signalPercent,
              lastSentOn:
                this.normalizeLastSentOn(summary.lastSentOn) ?? mod.lastSentOn,
              lastReadings: summary.lastReadings ?? mod.lastReadings
            };
          });
          this.updateMarkersOnMap();
        }),
        catchError(() => of(null))
      )
      .subscribe();
  }

  private loadAllModuleSummaries(): void {
    const fincaIds = Array.from(
      new Set(
        (this.fincas || [])
          .map(f => Number(f.idFinca))
          .filter(id => Number.isFinite(id) && id > 0)
      )
    );
    if (!fincaIds.length) {
      return;
    }

    const requests = fincaIds.map(id =>
      this.$http.default
        .get(`/admin/module/summary?fincaId=${encodeURIComponent(String(id))}`)
        .pipe(catchError(() => of([])))
    );

    forkJoin(requests)
      .pipe(
        tap(results => {
          const byId = new Map<number, any>();
          results.forEach(list => {
            if (!Array.isArray(list)) return;
            list.forEach(item => {
              const id = Number(item?.idModule);
              if (!Number.isFinite(id)) return;
              byId.set(id, item);
            });
          });

          this.modulos = this.modulos.map(mod => {
            const summary = byId.get(Number(mod.idModule));
            if (!summary) return mod;
            return {
              ...mod,
              batteryPercent: summary.batteryPercent ?? mod.batteryPercent,
              signalPercent: summary.signalPercent ?? mod.signalPercent,
              lastSentOn:
                this.normalizeLastSentOn(summary.lastSentOn) ?? mod.lastSentOn,
              lastReadings: summary.lastReadings ?? mod.lastReadings
            };
          });
          this.updateMarkersOnMap();
        })
      )
      .subscribe();
  }

  private normalizeLastSentOn(value: any): Date | string | null {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value === 'string') {
      const parsed = this.parseTimestamp(value);
      if (parsed !== null) {
        return new Date(parsed);
      }
      const fallback = Date.parse(value);
      if (Number.isFinite(fallback)) {
        return new Date(fallback);
      }
      return value;
    }
    return value;
  }

  private updateSensorAvailability(payload: any): void {
    const readings = Array.isArray(payload?.lastReadings)
      ? payload.lastReadings
      : [];
    const types = new Set(
      readings.map((r: any) => r?.sensorType).filter(Boolean)
    );
    this.showHumidity =
      types.has('HU') || types.has('E8') || types.has('SH') || types.has('SC');
    this.showIrrigation = types.has('FL');
    this.showTemperature = types.has('TE') || types.has('SH') || types.has('SC');
    this.showWind = types.has('AN');
    this.showRain = types.has('PL');
    this.showDigital = types.has('ED') || types.has('DIGITAL');
    this.showConductivity =
      types.has('E8') ||
      types.has('SC') ||
      payload?.conductivity?.CO_cond !== undefined ||
      payload?.conductivity?.CO_solution !== undefined;

    const huReading =
      readings.find((reading: any) =>
        ['HU', 'E8', 'SH', 'SC'].includes(reading?.sensorType)
      ) || null;
    const sensorId = Number(
      huReading?.idSensor ?? huReading?.sensorId ?? huReading?.id
    );
    this.humidityReadingAt =
      huReading?.createdOn ?? payload?.lastSentOn ?? null;
    this.humiditySensorId = Number.isFinite(sensorId) ? sensorId : null;
    if (this.humiditySensorId) {
      this.requestMoistureZones(
        this.humiditySensorId,
        false,
        true,
        this.humidityReadingAt
      );
    }
  }

  private updateMetrics(payload: any) {
    const hasFreshData = this.hasFreshDataForSelectedRange(payload);
    if (!hasFreshData) {
      this.soilMoistureValue = null;
      this.temperatureValue = null;
      this.windValue = null;
      this.dailyIrrigationValue = null;
      this.dailyRainValue = null;
      this.digitalValue = null;
      this.conductividad = null;
      this.conductividadSolution = null;
      this.renderSensorChart();
      return;
    }

    const soilMoisture = this.getHumidityControlValue(payload);
    const temperature = this.getTemperatureValue(payload);
    const windSpeed = this.toNumber(payload?.windSpeed);
    const dailyIrrigation = this.toNumber(payload?.dailyIrrigation);
    const dailyRain = this.toNumber(payload?.dailyRain);
    const digitalValue = this.getDigitalValue(payload);
    const conductivityValue = this.toNumber(payload?.conductivity?.CO_cond);
    const conductivitySolution = this.toNumber(
      payload?.conductivity?.CO_solution
    );
    const soilConductivity = this.getSoilConductivityValue(payload);

    this.soilMoistureValue = soilMoisture;
    this.temperatureValue = temperature;
    this.windValue = windSpeed;
    this.dailyIrrigationValue = dailyIrrigation;
    this.dailyRainValue = dailyRain;
    this.digitalValue = digitalValue;
    this.conductividad = conductivityValue ?? soilConductivity;
    this.conductividadSolution = conductivitySolution;

    this.renderSensorChart();
  }

  private getDigitalValue(payload: any): number | null {
    const readings = Array.isArray(payload?.lastReadings)
      ? payload.lastReadings
      : [];
    const digitalReading =
      readings.find((reading: any) =>
        ['ED', 'DIGITAL'].includes(String(reading?.sensorType || ''))
      ) || null;
    const rawValue =
      digitalReading?.data?.ed ??
      digitalReading?.data?.value ??
      digitalReading?.ed ??
      digitalReading?.value;
    return this.toNumber(rawValue);
  }
  private getHumidityControlValue(payload: any): number | null {
    const readings = Array.isArray(payload?.lastReadings)
      ? payload.lastReadings
      : [];
    const humidityReading = readings.find((reading: any) =>
      ['HU', 'E8', 'SH', 'SC'].includes(reading?.sensorType)
    );
    const controlValue =
      humidityReading?.data?.control ??
      humidityReading?.data?.hum ??
      humidityReading?.data?.SH_hum ??
      humidityReading?.data?.SC_hum ??
      humidityReading?.data?.H10;
    const parsed = this.toNumber(controlValue);
    if (parsed !== null) {
      return parsed;
    }
    return this.toNumber(payload?.precipitation);
  }

  private getSoilConductivityValue(payload: any): number | null {
    const readings = Array.isArray(payload?.lastReadings)
      ? payload.lastReadings
      : [];
    const conductivityReading =
      readings.find((reading: any) => reading?.sensorType === 'SC') || null;
    return this.toNumber(
      conductivityReading?.data?.ec ??
        conductivityReading?.data?.cond ??
        conductivityReading?.data?.SC_ec ??
        conductivityReading?.data?.SC_cond ??
        conductivityReading?.ec ??
        conductivityReading?.cond ??
        conductivityReading?.SC_ec ??
        conductivityReading?.SC_cond
    );
  }

  private getTemperatureValue(payload: any): number | null {
    const topLevel = this.toNumber(payload?.temperature);
    if (topLevel !== null) {
      return topLevel;
    }

    const readings = Array.isArray(payload?.lastReadings)
      ? payload.lastReadings
      : [];
    const temperatureReading =
      readings.find((reading: any) =>
        ['TE', 'SH', 'SC'].includes(String(reading?.sensorType || ''))
      ) || null;

    return this.toNumber(
      temperatureReading?.data?.temp ??
        temperatureReading?.data?.temperature ??
        temperatureReading?.data?.SC_temp ??
        temperatureReading?.data?.value ??
        temperatureReading?.temp ??
        temperatureReading?.value
    );
  }

  private getHighchartsLang(): any {
    return {
      months: this.translate.instant('STATICS.HIGHCHARTS.MONTHS'),
      shortMonths: this.translate.instant('STATICS.HIGHCHARTS.SHORT_MONTHS'),
      weekdays: this.translate.instant('STATICS.HIGHCHARTS.WEEKDAYS')
    };
  }

  private setHighchartOptions(): void {
    const locale =
      this.translate.currentLang || this.translate.getDefaultLang();
    Highcharts.setOptions({
      time: {
        useUTC: false,
        locale
      } as any,
      lang: this.getHighchartsLang() as any
    });
  }

  private renderSensorChart() {
    this.chartRenderId += 1;
    const renderId = this.chartRenderId;
    if (this.chartRenderHandle) {
      clearTimeout(this.chartRenderHandle);
    }
    this.chartRenderHandle = setTimeout(() => {
      if (renderId !== this.chartRenderId) {
        return;
      }
      const series = this.dailySeries;
      this.sensorChartHasData = series.length > 0;
      const container = document.getElementById('sensor-main-chart');
      if (!container) {
        return;
      }

      if (this.sensorChart) {
        try {
          this.sensorChart.destroy();
        } catch {
          // Avoid hard crash if Highcharts gets into an inconsistent state.
        }
        this.sensorChart = null;
      }

      if (!this.sensorChartHasData) {
        return;
      }

      const wrapper = container.parentElement as HTMLElement | null;
      const height = wrapper?.clientHeight || container.clientHeight || 240;
      const textColor = this.getCssVar('--app-text', '#e5e7eb');
      const mutedColor = this.getCssVar('--app-muted', '#9ca3af');
      const borderColor = this.getCssVar('--app-border', '#334155');
      const borderStrong = this.getCssVar('--app-border-strong', '#94a3b8');
      const surface = this.getCssVar('--app-surface', '#0f172a');

      this.sensorChart = Highcharts.chart(container, {
        chart: {
          type: 'spline',
          backgroundColor: surface,
          height,
          spacing: [10, 8, 6, 8]
        },
        title: undefined,
        credits: { enabled: false },
        legend: {
          enabled: true,
          align: 'left',
          verticalAlign: 'top',
          itemStyle: { color: textColor },
          itemHoverStyle: { color: textColor },
          itemHiddenStyle: { color: mutedColor }
        },
        exporting: { enabled: false },
        tooltip: {
          shared: true,
          valueDecimals: 2,
          xDateFormat: '%e %b %Y, %H:%M',
          backgroundColor: surface,
          borderColor: borderStrong,
          borderWidth: 1,
          style: { color: textColor }
        },
        xAxis: {
          type: 'datetime',
          labels: { style: { color: mutedColor } },
          tickLength: 4
        },
        yAxis: [
          {
            id: 'humidityControl',
            title: undefined,
            labels: { style: { color: '#16a34a' } },
            gridLineColor: borderColor
          },
          {
            id: 'humidityDrain',
            title: undefined,
            labels: { style: { color: '#10b981' } },
            gridLineColor: borderColor,
            opposite: true
          },
          {
            id: 'temperature',
            title: undefined,
            labels: { style: { color: '#f97316' } },
            gridLineColor: borderColor
          },
          {
            id: 'windSpeed',
            title: undefined,
            labels: { style: { color: '#0ea5e9' } },
            gridLineColor: borderColor,
            opposite: true
          }
        ],
        plotOptions: {
          spline: {
            marker: { enabled: false },
            lineWidth: 2
          }
        },
        series
      } as Highcharts.Options);
    });
  }

  private renderMap() {
    const container = this.mapContainer?.nativeElement;
    if (!container) {
      return;
    }

    (mapboxgl as any).accessToken = this.mapboxToken;

    if (!this.map) {
      this.map = new mapboxgl.Map({
        container,
        style: this.mapStyle,
        center: this.defaultCenter,
        zoom: this.defaultZoom,
        pitch: 0,
        bearing: 0
      });
      this.mapReady = false;
      this.map.once('load', () => {
        this.mapReady = true;
        this.updateMarkersOnMap();
      });
      return;
    }

    if (!this.mapReady && this.map.loaded()) {
      this.mapReady = true;
    }
    this.updateMarkersOnMap();
  }

  private updateMarkersOnMap() {
    if (!this.map || !this.mapReady) {
      return;
    }

    this.markers.forEach(marker => marker.remove());
    this.markers = [];

    const valid = this.modulos
      .map(mod => {
        const humidity = this.extractHumidityReading(mod);
        if (
          humidity.sensorId &&
          !this.moistureZonesCache.has(humidity.sensorId)
        ) {
          this.requestMoistureZones(
            humidity.sensorId,
            true,
            false,
            humidity.createdOn
          );
        }
        return {
          id: mod.idModule,
          name: mod.name,
          lat: this.toNumber(mod.latitude),
          lng: this.toNumber(mod.longitude),
          battery: this.toNumber(mod.batteryPercent ?? mod.batteryCapacity),
          coverage: this.toNumber(
            mod.signalPercent ?? mod.coverage ?? mod.signalStrength
          ),
          markerColor: this.getMarkerColor(humidity)
        };
      })
      .filter(
        m =>
          m.lat !== null &&
          m.lng !== null &&
          Math.abs(m.lat) <= 90 &&
          Math.abs(m.lng) <= 180
      ) as Array<{
      id: number;
      name: string;
      lat: number;
      lng: number;
      battery: number | null;
      coverage: number | null;
      markerColor: string;
    }>;

    if (!valid.length) {
      this.map.setCenter(this.defaultCenter);
      this.map.setZoom(this.defaultZoom);
      return;
    }

    valid.forEach(m => {
      const marker = new mapboxgl.Marker({
        color: m.markerColor,
        scale: 1.1
      })
        .setLngLat([m.lng, m.lat])
        .addTo(this.map as mapboxgl.Map);

      marker.setPopup(
        new mapboxgl.Popup({
          offset: 18,
          closeButton: false,
          closeOnClick: false
        }).setText(m.name || '')
      );
      marker
        .getElement()
        .addEventListener('mouseenter', () => marker.togglePopup());
      marker
        .getElement()
        .addEventListener('mouseleave', () => marker.togglePopup());
      marker
        .getElement()
        .addEventListener('click', () => this.moduloChange(m.id));
      this.markers.push(marker);
    });

    const bounds = new mapboxgl.LngLatBounds();
    valid.forEach(m => bounds.extend([m.lng, m.lat]));
    if (!bounds.isEmpty()) {
      this.map.fitBounds(bounds, { padding: 60, maxZoom: 16 });
    } else {
      this.map.setCenter(this.defaultCenter);
      this.map.setZoom(this.defaultZoom);
    }
  }

  private extractHumidityReading(
    mod: any
  ): { sensorId: number | null; control: number | null; createdOn: any } {
    const readings = Array.isArray(mod?.lastReadings) ? mod.lastReadings : [];
    const huReading =
      readings.find((reading: any) =>
        ['HU', 'E8', 'SH', 'SC'].includes(reading?.sensorType)
      ) || null;
    const control = this.toNumber(
      huReading?.data?.control ??
        huReading?.data?.hum ??
        huReading?.data?.SH_hum ??
        huReading?.data?.SC_hum ??
        huReading?.data?.H10 ??
        huReading?.control ??
        huReading?.data?.value ??
        huReading?.value
    );
    const rawId = huReading?.idSensor ?? huReading?.sensorId ?? huReading?.id;
    const sensorId = Number(rawId);
    return {
      sensorId: Number.isFinite(sensorId) ? sensorId : null,
      control,
      createdOn: huReading?.createdOn ?? mod?.lastSentOn ?? null
    };
  }

  private getMarkerColor(humidity: {
    sensorId: number | null;
    control: number | null;
  }): string {
    if (!humidity.sensorId || humidity.control === null) {
      return this.fallbackMarkerColor;
    }
    // Not yet fetched — stay gray until the async response arrives.
    if (!this.moistureZonesCache.has(humidity.sensorId)) {
      return this.fallbackMarkerColor;
    }
    const zonesResponse =
      this.moistureZonesCache.get(humidity.sensorId) ?? null;
    // Fall back to default zones when the API returns no season/profile
    // (e.g. SEASON_NOT_FOUND), consistent with how the chart handles it.
    const zones =
      zonesResponse?.zones?.length ? zonesResponse.zones : this.defaultHuZones;
    const color = this.resolveZoneColor(humidity.control, zones);
    return color || this.fallbackMarkerColor;
  }

  private resolveZoneColor(
    value: number,
    zones: Array<{ value?: number; color?: string }>
  ): string | null {
    const sorted = [...zones].sort((a, b) => {
      const av = a.value ?? Number.POSITIVE_INFINITY;
      const bv = b.value ?? Number.POSITIVE_INFINITY;
      return av - bv;
    });
    for (const zone of sorted) {
      if (zone.value === undefined || value <= zone.value) {
        return zone.color || null;
      }
    }
    return sorted.length ? sorted[sorted.length - 1].color || null : null;
  }

  private requestMoistureZones(
    sensorId: number,
    refreshMap = true,
    refreshChart = false,
    atValue?: any
  ): void {
    if (this.moistureZonesCache.has(sensorId)) {
      if (refreshMap) {
        this.updateMarkersOnMap();
      }
      if (refreshChart) {
        this.renderSensorChart();
      }
      return;
    }

    // Accumulate deferred refresh intentions so they are not lost when
    // a concurrent pending request for the same sensorId is already in flight.
    if (refreshMap) {
      this.moistureZonesDeferredMap.add(sensorId);
    }
    if (refreshChart) {
      this.moistureZonesDeferredChart.add(sensorId);
    }

    if (this.moistureZonesPending.has(sensorId)) {
      return;
    }

    this.moistureZonesPending.add(sensorId);
    const at = this.resolveZonesDate(atValue);
    this.sensorMoistureZonesService.getZones(sensorId, at).subscribe(result => {
      this.moistureZonesCache.set(sensorId, result);
      this.moistureZonesPending.delete(sensorId);

      const doMap = this.moistureZonesDeferredMap.has(sensorId);
      const doChart = this.moistureZonesDeferredChart.has(sensorId);
      this.moistureZonesDeferredMap.delete(sensorId);
      this.moistureZonesDeferredChart.delete(sensorId);

      if (doMap) {
        this.updateMarkersOnMap();
      }
      if (doChart) {
        this.renderSensorChart();
      }
    });
  }

  private formatZonesDate(date: Date): string {
    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    return `${year}-${month}-${day}`;
  }

  private resolveZonesDate(value: any): string {
    if (value instanceof Date) {
      return this.formatZonesDate(value);
    }
    if (typeof value === 'string' && value.trim()) {
      const parsed = this.parseTimestamp(value);
      if (parsed !== null) {
        return this.formatZonesDate(new Date(parsed));
      }
      const fallback = Date.parse(value);
      if (Number.isFinite(fallback)) {
        return this.formatZonesDate(new Date(fallback));
      }
    }
    return this.formatZonesDate(new Date());
  }

  private getHumidityZones(): Array<{ value?: number; color?: string }> | null {
    if (!this.humiditySensorId) return null;
    const response = this.moistureZonesCache.get(this.humiditySensorId) || null;
    return response?.zones || null;
  }

  private destroyMap() {
    if (this.map) {
      this.map.remove();
      this.map = null;
      this.mapReady = false;
      this.markers = [];
    }
  }

  private toNumber(value: any): number | null {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value === 'string') {
      const normalized = value.replace(',', '.').trim();
      const parsed = Number(normalized);
      return Number.isFinite(parsed) ? parsed : null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private clampPercent(value: any): number {
    const parsed = this.toNumber(value);
    if (parsed === null) {
      return 0;
    }
    return Math.min(100, Math.max(0, parsed));
  }

  public getBatteryLevel(value: any): number {
    return this.clampPercent(value);
  }

  public getBatteryColor(value: any): string {
    const level = this.clampPercent(value);
    if (level <= 20) {
      return '#dc2626';
    }
    if (level <= 50) {
      return '#f59e0b';
    }
    if (level <= 80) {
      return '#84cc16';
    }
    return '#16a34a';
  }

  public formatBattery(value: any): string {
    const parsed = this.toNumber(value);
    if (parsed === null) {
      return '-';
    }
    return `${Math.round(parsed)}%`;
  }

  private updateModuleStats(moduloId: number, payload: any): void {
    if (!moduloId || !payload) {
      return;
    }
    const target = this.modulos.find(mod => Number(mod.idModule) == moduloId);
    if (!target) {
      return;
    }
    if (payload.batteryPercent !== undefined) {
      target.batteryPercent = payload.batteryPercent;
    }
    if (payload.signalPercent !== undefined) {
      target.signalPercent = payload.signalPercent;
    }
    if (payload.lastSentOn !== undefined) {
      target.lastSentOn = payload.lastSentOn;
    }
    if (payload.lastReadings !== undefined) {
      target.lastReadings = payload.lastReadings;
    }
  }

  private destroySensorChart() {
    if (this.sensorChart) {
      this.sensorChart.destroy();
      this.sensorChart = null;
    }
  }

  private buildDailySeries(daily: any): Highcharts.SeriesOptionsType[] {
    const seriesDefs = [
      {
        key: 'humidityControl',
        name: this.translate.instant('HOME.CHART.HUMIDITY'),
        color: '#16a34a',
        yAxis: 'humidityControl'
      },
      {
        key: 'temperature',
        name: this.translate.instant('HOME.CHART.TEMPERATURE'),
        color: '#f97316',
        yAxis: 'temperature'
      },
      {
        key: 'windSpeed',
        name: this.translate.instant('HOME.CHART.WIND'),
        color: '#0ea5e9',
        yAxis: 'windSpeed'
      }
    ].filter(def => {
      if (def.key === 'humidityControl') {
        return this.showHumidity;
      }
      if (def.key === 'temperature') {
        return this.showTemperature;
      }
      if (def.key === 'windSpeed') {
        return this.showWind;
      }
      return true;
    });

    return seriesDefs
      .map(def => {
        const points: Array<[number, number]> = (daily?.[def.key] || [])
          .map((entry: any) => this.toPoint(entry))
          .filter((p: any): p is [number, number] => p !== null);
        if (!points.length) {
          return null;
        }
        const series: Highcharts.SeriesSplineOptions = {
          type: 'spline',
          name: def.name,
          data: points,
          color: def.color,
          yAxis: def.yAxis,
          tooltip: { valueDecimals: 1 }
        };
        if (def.key === 'humidityControl') {
          const zones = this.getHumidityZones() || this.defaultHuZones;
          (series as any).zoneAxis = 'y';
          (series as any).zones = zones;
        }
        return series;
      })
      .filter((s): s is Highcharts.SeriesSplineOptions => !!s);
  }

  private toPoint(entry: any): [number, number] | null {
    if (!entry || entry.length < 2) {
      return null;
    }
    const [rawTs, rawVal] = entry;
    const ts = this.parseTimestamp(rawTs);
    const y = this.toNumber(rawVal);
    if (ts === null || y === null) {
      return null;
    }
    return [ts, y];
  }

  private parseTimestamp(ts: string): number | null {
    if (!ts || typeof ts !== 'string') {
      return null;
    }
    const match = ts.match(
      /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})([+-])(\d{2}):?(\d{2})$/
    );
    if (match) {
      const [, y, m, d, hh, mm, ss, sign, tzh, tzm] = match;
      const year = Number(y);
      const month = Number(m) - 1;
      const day = Number(d);
      const hour = Number(hh);
      const minute = Number(mm);
      const second = Number(ss);
      const offsetMinutes = Number(tzh) * 60 + Number(tzm);
      const utcMillis =
        Date.UTC(year, month, day, hour, minute, second) -
        (sign === '+' ? offsetMinutes : -offsetMinutes) * 60 * 1000;
      return Number.isFinite(utcMillis) ? utcMillis : null;
    }
    const fallback = Date.parse(ts);
    return Number.isFinite(fallback) ? fallback : null;
  }

  private hasFreshDataForSelectedRange(payload: any): boolean {
    const referenceMs = this.getPayloadReferenceTimestamp(payload);
    const rangeStartMs = this.getSelectedRangeStartMs();
    return referenceMs !== null && rangeStartMs !== null && referenceMs >= rangeStartMs;
  }

  private getPayloadReferenceTimestamp(payload: any): number | null {
    const candidates: number[] = [];
    const lastSentMs = this.parseTimestamp(String(payload?.lastSentOn || ''));
    if (lastSentMs !== null) {
      candidates.push(lastSentMs);
    }

    const readings = Array.isArray(payload?.lastReadings) ? payload.lastReadings : [];
    for (const reading of readings) {
      const readingMs = this.parseTimestamp(String(reading?.createdOn || ''));
      if (readingMs !== null) {
        candidates.push(readingMs);
      }
    }

    if (!candidates.length) {
      return null;
    }

    return Math.max(...candidates);
  }

  private getSelectedRangeStartMs(): number | null {
    const now = Date.now();
    switch (this.selectedRange) {
      case '24h':
        return now - 24 * 60 * 60 * 1000;
      case '7d':
        return now - 7 * 24 * 60 * 60 * 1000;
      case '30d':
        return now - 30 * 24 * 60 * 60 * 1000;
      default:
        return null;
    }
  }
}
